// src/pages/GatewayDetailPage.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  Modal
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { formatDate } from '../utils/format';

const PAGE_SIZE = 20;
const STATUS_POLL_INTERVAL_MS = 5000;
const OBJECT_ID_REGEX = /^[0-9a-fA-F]{24}$/;

export default function GatewayDetailPage() {
  const { gatewayId: paramGatewayId } = useParams();
  const [realId, setRealId] = useState(paramGatewayId.trim());
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const [gateway, setGateway] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ gpsCoordinates: '', phoneNumber: '' });
  const [formErrors, setFormErrors] = useState({ phoneNumber: '' });
  const [bleActivities, setBleActivities] = useState([]);
  const [peripherals, setPeripherals] = useState([]);
  const [gwActs, setGwActs] = useState([]);
  const [gwStart, setGwStart] = useState(null);
  const [loadingMoreGw, setLoadingMoreGw] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [subActs, setSubActs] = useState([]);
  const [subStart, setSubStart] = useState(null);
  const [loadingMoreSub, setLoadingMoreSub] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // AbortController refs
  const gatewayAbortControllerRef = useRef();
  const statusAbortControllerRef = useRef();
  const bleAbortControllerRef = useRef();
  const peripheralsAbortControllerRef = useRef();
  const gwActsAbortControllerRef = useRef();
  const subActsAbortControllerRef = useRef();
  const statusIntervalRef = useRef(null);

  const formatGps = (value) => {
    const coordsArr = value
      .split(',')
      .map(c => parseFloat(c.trim()));
    if (coordsArr.length === 2 && coordsArr.every(n => !isNaN(n))) {
      return `${coordsArr[0].toFixed(6)}, ${coordsArr[1].toFixed(6)}`;
    }
    return value;
  };

  const resetForm = () => {
    const raw = gateway?.gpsCoordinates || '';
    const coords = String(raw)
      .split(',')
      .map(c => parseFloat(c.trim()));
    const formatted = (coords.length === 2 && coords.every(n => !isNaN(n)))
      ? `${coords[0].toFixed(6)}, ${coords[1].toFixed(6)}`
      : '';
    setFormData({
      gpsCoordinates: formatted,
      phoneNumber: gateway?.phoneNumber || ''
    });
    setFormErrors({ phoneNumber: '' });
  };

  const handleEdit = () => {
    resetForm();
    setEditMode(true);
  };

  const fetchGateway = async () => {
    setError(null);
    gatewayAbortControllerRef.current?.abort();
    const controller = new AbortController();
    gatewayAbortControllerRef.current = controller;
    const signal = controller.signal;

    try {
      const raw = paramGatewayId.trim();
      let res;
      if (OBJECT_ID_REGEX.test(raw)) {
        res = await api.get(`/api/proxy/gateways/${raw}`, { signal });
        setRealId(raw);
      } else {
        const serial = raw.replace(/^GTW-/, '');
        res = await api.get(`/api/proxy/gateways/serial/${serial}`, { signal });
        const id = res.data._id?.$oid || res.data._id;
        setRealId(id);
      }

      const gw = res.data;
      setGateway(gw);

      const coords = String(gw.gpsCoordinates || '')
        .split(',')
        .map(c => parseFloat(c.trim()));
      const formatted = (coords.length === 2 && coords.every(n => !isNaN(n)))
        ? `${coords[0].toFixed(6)}, ${coords[1].toFixed(6)}`
        : '';
      setFormData({
        gpsCoordinates: formatted,
        phoneNumber: gw.phoneNumber || ''
      });
      setFormErrors({ phoneNumber: '' });
    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error(err);
        setError('Failed to load gateway.');
      }
    }
  };

  const fetchGatewayStatus = async () => {
    if (!OBJECT_ID_REGEX.test(realId)) return;

    statusAbortControllerRef.current?.abort();
    const controller = new AbortController();
    statusAbortControllerRef.current = controller;
    const signal = controller.signal;

    try {
      const res = await api.get(`/api/proxy/gateways/status/${realId}`, { signal });
      setGateway(prev => (prev ? { ...prev, ...res.data } : prev));
    } catch (err) {
      if (err.name !== 'CanceledError') console.error(err);
    }
  };

  const fetchBle = async () => {
    bleAbortControllerRef.current?.abort();
    const controller = new AbortController();
    bleAbortControllerRef.current = controller;
    const signal = controller.signal;

    try {
      const res = await api.get(`/api/proxy/gateways/ble-activity/${realId}`, { signal });
      setBleActivities(res.data);
    } catch (err) {
      if (err.name !== 'CanceledError') console.error(err);
    }
  };

  const fetchPeripherals = async () => {
    peripheralsAbortControllerRef.current?.abort();
    const controller = new AbortController();
    peripheralsAbortControllerRef.current = controller;
    const signal = controller.signal;

    try {
      const res = await api.get(`/api/proxy/gateways/peripheral-discovery/${realId}`, { signal });
      setPeripherals(res.data);
    } catch (err) {
      if (err.name !== 'CanceledError') console.error(err);
    }
  };

  const fetchGwActs = async (start) => {
    if (start) setLoadingMoreGw(true);
    gwActsAbortControllerRef.current?.abort();
    const controller = new AbortController();
    gwActsAbortControllerRef.current = controller;
    const signal = controller.signal;
    const params = { max: PAGE_SIZE };
    if (start) params.start = start.toISOString();

    try {
      const res = await api.get(`/api/proxy/gateways/gateway-activity/${realId}`, { params, signal });
      const items = res.data;
      setGwActs(prev => start ? [...prev, ...items] : items);
      if (items.length) setGwStart(new Date(items[items.length - 1].timestamp));
    } catch (err) {
      if (err.name !== 'CanceledError') console.error(err);
    } finally {
      if (start) setLoadingMoreGw(false);
    }
  };

  const fetchSubActs = async (meterId, start) => {
    if (start) setLoadingMoreSub(true);
    subActsAbortControllerRef.current?.abort();
    const controller = new AbortController();
    subActsAbortControllerRef.current = controller;
    const signal = controller.signal;
    const params = { max: PAGE_SIZE, meterId };
    if (start) params.start = start.toISOString();

    try {
      const res = await api.get(`/api/proxy/gateways/gateway-activity/${realId}`, { params, signal });
      const items = res.data;
      setSubActs(prev => start ? [...prev, ...items] : items);
      if (items.length) setSubStart(new Date(items[items.length - 1].timestamp));
    } catch (err) {
      if (err.name !== 'CanceledError') console.error(err);
    } finally {
      if (start) setLoadingMoreSub(false);
    }
  };

  const handleMeterRowClick = (meterId) => {
    setSelectedMeter(meterId);
    setSubActs([]);
    setSubStart(null);
    fetchSubActs(meterId, null);
  };

  const handleSave = async () => {
    if (!/^\d{10}$/.test(formData.phoneNumber)) {
      setFormErrors({ ...formErrors, phoneNumber: 'Phone number must be exactly 10 digits' });
      return;
    }

    try {
      const updates = {};

      if (formData.phoneNumber !== gateway.phoneNumber) {
        const check = await api.get(`/api/proxy/fieldcheck/${formData.phoneNumber}`, {
          params: { fieldName: 'phoneNumber' }
        });
        if (check.data.exists) {
          setFormErrors({ ...formErrors, phoneNumber: 'Phone number already in use' });
          return;
        }
        updates.phoneNumber = formData.phoneNumber;
      }

      const coords = formData.gpsCoordinates
        .split(',')
        .map(c => parseFloat(c.trim()))
        .filter(n => !isNaN(n));
      const originalCoords = Array.isArray(gateway.gpsCoordinates)
        ? gateway.gpsCoordinates
        : [];
      if (
        coords.length === 2 &&
        (
          !originalCoords.length ||
          coords[0] !== originalCoords[0] ||
          coords[1] !== originalCoords[1]
        )
      ) {
        updates.gpsCoordinates = coords;
      }

      if (Object.keys(updates).length === 0) {
        setEditMode(false);
        return;
      }

      const res = await api.patch(`/api/proxy/gateways/${realId}`, updates);
      setGateway(res.data);
      setEditMode(false);
      setSnackbar({ open: true, message: 'Saved successfully', severity: 'success' });
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        setSnackbar({ open: true, message: 'Save failed: Action denied', severity: 'error' });
      } else {
        setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
      }
    }
  };

  const handleDeleteClick = () => setDeleteModalOpen(true);
  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/api/proxy/gateways/${realId}`);
      navigate(-1);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        setSnackbar({ open: true, message: 'Delete failed: Action denied', severity: 'error' });
      } else {
        setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
      }
    }
  };

  useEffect(() => {
    fetchGateway();
    return () => {
      gatewayAbortControllerRef.current?.abort();
    };
  }, [paramGatewayId]);

  useEffect(() => {
    if (!OBJECT_ID_REGEX.test(realId)) return;

    fetchGatewayStatus();
    statusIntervalRef.current = setInterval(fetchGatewayStatus, STATUS_POLL_INTERVAL_MS);

    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
        statusIntervalRef.current = null;
      }
      statusAbortControllerRef.current?.abort();
    };
  }, [realId]);

  useEffect(() => {
    if (!gateway || !OBJECT_ID_REGEX.test(realId)) return;

    fetchBle();
    fetchPeripherals();
    fetchGwActs(null);

    return () => {
      bleAbortControllerRef.current?.abort();
      peripheralsAbortControllerRef.current?.abort();
      gwActsAbortControllerRef.current?.abort();
      subActsAbortControllerRef.current?.abort();
    };
  }, [gateway?._id, realId]);

  if (error && !gateway) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!gateway) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  const statusColor = gateway.isOnline ? 'green' : 'gray';
  const serialNumber = gateway.deviceName.replace(/^GTW-/, '');

  return (
    <Box p={2}>
      <IconButton onClick={() => navigate(-1)}>
        <ArrowBackIcon />
      </IconButton>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          {gateway.deviceName || `GTW-${serialNumber}`}
        </Typography>
        <FiberManualRecordIcon sx={{ color: statusColor, ml: 1 }} fontSize="large" />
      </Box>

      <Grid container spacing={2} sx={{
        [theme.breakpoints.up('md')]: { height: 'calc(100vh - 100px)' }
      }}>
        {/* Left column: base info */}
        <Grid item xs={12} md={4} sx={{
          display: 'flex',
          flexDirection: 'column',
          [theme.breakpoints.up('md')]: { height: '100%' }
        }}>
          <Paper sx={{ p: 2, mb: 2, flexShrink: 0 }}>
            {!editMode ? (
              <Box>
                <Typography noWrap sx={{ maxWidth: 'auto' }}>Device Id: {gateway._id}</Typography>
                <Typography>Device Name: {gateway.deviceName}</Typography>
                <Typography>Serial Number: {serialNumber}</Typography>
                <Typography>IMEI: {gateway.imei}</Typography>
                <Typography>BLE Address: {gateway.bleAddress}</Typography>
                <Typography>MAC Address: {gateway.macAddress}</Typography>
                <Typography>Phone Number: {gateway.phoneNumber}</Typography>
                <Typography>
                  Last Seen: {gateway.lastseen ? formatDate(gateway.lastseen, true) : 'Never'}
                </Typography>
                <Typography>
                  Registered At: {formatDate(gateway.registeredAt)}
                </Typography>
                <Typography>
                  Discovered Peripherals: {Math.trunc(gateway.discoveredPeripheralsCount)}
                </Typography>
                <Typography>
                  Pushed Records Count: {Math.trunc(gateway.recordsCount)}
                </Typography>
                <Typography>
                  Water Meters Count: {Math.trunc(gateway.waterMetersCount)}
                </Typography>
                {formData.gpsCoordinates && (
                  <Typography>GPS Location (longitude, latitude): {formData.gpsCoordinates}</Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Button onClick={handleEdit}>Edit</Button>
                  {!gateway.lastseen && (
                    <Button color="error" onClick={handleDeleteClick}>
                      Delete
                    </Button>
                  )}
                </Box>
              </Box>
            ) : (
              <Box>
                <TextField
                  label="GPS Coordinates (longitude, latitude)"
                  fullWidth
                  value={formData.gpsCoordinates}
                  onChange={e => setFormData({ ...formData, gpsCoordinates: e.target.value })}
                  onBlur={e => setFormData({ ...formData, gpsCoordinates: formatGps(e.target.value) })}
                  helperText="Format: longitude,latitude — e.g. 2.418300, 6.365400"
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Phone Number"
                  fullWidth
                  value={formData.phoneNumber}
                  onChange={e => {
                    setFormData({ ...formData, phoneNumber: e.target.value });
                    setFormErrors({ ...formErrors, phoneNumber: '' });
                  }}
                  onBlur={e => {
                    if (!/^\d{10}$/.test(e.target.value)) {
                      setFormErrors({ ...formErrors, phoneNumber: 'Phone number must be exactly 10 digits' });
                    }
                  }}
                  error={!!formErrors.phoneNumber}
                  helperText={formErrors.phoneNumber}
                  sx={{ mb: 2 }}
                />
                <Button onClick={handleSave}>Save</Button>
                <Button onClick={() => setEditMode(false)}>Cancel</Button>
              </Box>
            )}
          </Paper>

          {peripherals.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Discovered Peripherals
              </Typography>
              <Paper sx={{ p: 1, mb: 2, flexShrink: 0 }}>
                <Grid container sx={{ fontWeight: 'bold', mb: 1 }}>
                  <Grid item xs={isSmall ? 6 : 4}><Typography>Name</Typography></Grid>
                  <Grid item xs={isSmall ? 6 : 4}><Typography>BLE Address</Typography></Grid>
                  {!isSmall && (
                    <Grid item xs={4}><Typography>Timestamp</Typography></Grid>
                  )}
                </Grid>
                {peripherals.map((p, i) => (
                  <Grid container key={i} sx={{ mb: 0.5 }}>
                    <Grid item xs={isSmall ? 6 : 4}>
                      <Typography noWrap sx={{ maxWidth: 'auto' }}>
                        {p.deviceName}
                      </Typography>
                    </Grid>
                    <Grid item xs={isSmall ? 6 : 4}>
                      <Typography>{p.bleAddress}</Typography>
                    </Grid>
                    {!isSmall && (
                      <Grid item xs={4}><Typography>{formatDate(p.timestamp)}</Typography></Grid>
                    )}
                  </Grid>
                ))}
              </Paper>
            </>
          )}
        </Grid>

        <Grid item xs={12} md={8} sx={{
          display: 'flex',
          flexDirection: 'column',
          [theme.breakpoints.up('md')]: { height: '100%' }
        }}>
          {bleActivities.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                BLE Activities
              </Typography>
              <Paper sx={{ p: 1, mb: 2, flexShrink: 0 }}>
                <Grid container sx={{ fontWeight: 'bold', mb: 1 }}>
                  <Grid item xs={6}><Typography>Meter</Typography></Grid>
                  <Grid item xs={4}><Typography>Timestamp</Typography></Grid>
                  <Grid item xs={2}><Typography>RSSI</Typography></Grid>
                </Grid>
                {bleActivities.map((b, i) => (
                  <Grid
                    container
                    key={i}
                    sx={{ mb: 0.5, cursor: 'pointer' }}
                    onClick={() => navigate(`/water-meters/${b.meterId}`)}
                  >
                    <Grid item xs={6}>
                      <Typography noWrap sx={{ maxWidth: 'auto' }}>
                        {b.meterId}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography>{formatDate(b.timestamp)}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography sx={{
                        color:
                          Math.trunc(b.rssi) > -60 ? 'green' :
                          Math.trunc(b.rssi) > -80 ? 'orange' :
                          'red'
                      }}>
                        {Math.trunc(b.rssi)}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </Paper>
            </>
          )}

          {selectedMeter ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }} noWrap>
                  Activities for Meter {selectedMeter}
                </Typography>
                <Button onClick={() => setSelectedMeter(null)}>Back</Button>
              </Box>
              <Paper sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
                <Grid container sx={{ fontWeight: 'bold', mb: 1 }}>
                  <Grid item xs={3}><Typography>Meter</Typography></Grid>
                  <Grid item xs={3}><Typography>Topic</Typography></Grid>
                  <Grid item xs={3}><Typography>Action</Typography></Grid>
                  <Grid item xs={3}><Typography>Timestamp</Typography></Grid>
                </Grid>
                {subActs.map((g, i) => (
                  <Grid container key={i} sx={{ mb: 0.5 }}>
                    <Grid item xs={3}>
                      <Typography noWrap sx={{ maxWidth: 'auto' }}>
                        {g.meterId || 'NA'}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography noWrap sx={{ maxWidth: 'auto' }}>
                        {g.mqttTopic?.slice(0, 20) || 'NA'}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography noWrap sx={{ maxWidth: 'auto' }}>
                        {g.action?.slice(0, 25)}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography>{formatDate(g.timestamp)}</Typography>
                    </Grid>
                  </Grid>
                ))}
                <Box textAlign="center" mt={1}>
                  <Button
                    variant="outlined"
                    onClick={() => fetchSubActs(selectedMeter, subStart)}
                    disabled={loadingMoreSub}
                  >
                    {loadingMoreSub ? 'Loading…' : 'Load More'}
                  </Button>
                </Box>
              </Paper>
            </>
          ) : (
            gwActs.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom>
                  Gateway Activities
                </Typography>
                <Paper sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
                  <Grid container sx={{ fontWeight: 'bold', mb: 1 }}>
                    <Grid item xs={3}><Typography>Meter</Typography></Grid>
                    <Grid item xs={3}><Typography>Topic</Typography></Grid>
                    <Grid item xs={3}><Typography>Action</Typography></Grid>
                    <Grid item xs={3}><Typography>Timestamp</Typography></Grid>
                  </Grid>
                  {gwActs.map((g, i) => (
                    <Grid
                      container
                      key={i}
                      sx={{ mb: 0.5, cursor: 'pointer' }}
                      onClick={() => handleMeterRowClick(g.meterId)}
                    >
                      <Grid item xs={3}>
                        <Typography noWrap sx={{ maxWidth: 'auto' }}>
                          {g.meterId || 'NA'}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography noWrap sx={{ maxWidth: 'auto' }}>
                          {g.mqttTopic?.slice(0, 20) || 'NA'}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography noWrap sx={{ maxWidth: 'auto' }}>
                          {g.action?.slice(0, 25)}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography>{formatDate(g.timestamp)}</Typography>
                      </Grid>
                    </Grid>
                  ))}
                  <Box textAlign="center" mt={1}>
                    <Button
                      variant="outlined"
                      onClick={() => fetchGwActs(gwStart)}
                      disabled={loadingMoreGw}
                    >
                      {loadingMoreGw ? 'Loading…' : 'Load More'}
                    </Button>
                  </Box>
                </Paper>
              </>
            )
          )}
        </Grid>
      </Grid>

      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            bgcolor: 'background.paper',
            p: 2
          }}
        >
          <Typography variant="h6">Confirm Delete</Typography>
          <Typography>Are you sure you want to delete this device?</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleConfirmDelete}>Delete</Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}